import { useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { map, mapValues, some, isEmpty, isEqual } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses, OK } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { useIntradayPriceStream } from "connected-components/dashboard/hooks/use-intraday-price"
import { useIntradayStandalonePerformanceStream } from "connected-components/dashboard/hooks/use-intraday-standalone-performance"
import { buildIntradayFlatMap, buildIntradayStandaloneFlatMap } from "gatherers/contributors/intraday"
import { collectLeafModelProviderIds } from "helpers/contributors"
import { addIntradayPriceTooltipContents } from "helpers/metrics"

const EMPTY_ARRAY = []

function zipRecursive(zipped, result) {
  if (!zipped) {
    zipped = {
      label: result.label,
      values: {},
    }
  }

  for (const category in result.values) {
    zipped.values[category] = result.values[category]
  }

  // Sometimes a contributor shows up as notHeld in the composition query, but is held in the performance query
  // We want to show these securities as no longer held in the application
  // This is the case for securities that only have a pnl value, and no economic exposure
  if (zipped.notHeld && result.isNoLongerHeld) {
    zipped.isNoLongerHeld = result.isNoLongerHeld
  }

  if (result.children) {
    if (!zipped.children) {
      zipped.children = {}
    }

    for (const childId in result.children) {
      const child = result.children[childId]
      if (!zipped.children[childId]) {
        zipped.children[childId] = {
          id: child.id,
          isFactor: child.isFactor,
          label: child.label,
          notHeld: child.notHeld,
          isUnmodeled: child.isUnmodeled,
          isNoLongerHeld: child.isNoLongerHeld,
          values: {},
          pinnedRowOrder: child.pinnedRowOrder,
          tooltipValue: child.tooltipValue,
        }
      }
      zipRecursive(zipped.children[childId], child)
    }
  }

  return zipped
}

export function zipResults(results) {
  return results.reduce((acc, result) => zipRecursive(acc, result), null)
}

function mergeFlatContributorsIntoRow(row, flatContributors, rowIdForKey) {
  if (!row) return row
  const lookupId = rowIdForKey ?? row?.id

  let values = row.values
  for (const { prefix, valuesByRowId } of flatContributors) {
    const next = lookupId == null ? undefined : valuesByRowId?.[lookupId]
    if (!next) continue
    const prev = values?.[prefix]
    const contributorDidChange = !isEqual(prev, next)
    if (!contributorDidChange) continue
    if (values === row.values) values = { ...(row.values || {}) }
    values[prefix] = next
  }

  let children = row.children
  if (row.children) {
    const newChildren = mapValues(row.children, (child, childKey) =>
      mergeFlatContributorsIntoRow(child, flatContributors, childKey),
    )
    if (some(newChildren, (child, key) => child !== row.children[key])) children = newChildren
  }

  if (values === row.values && children === row.children) return row
  return { ...row, values, children }
}

function mergeFlatContributors(data, flatContributors) {
  if (!data || !flatContributors?.length) return data
  return mergeFlatContributorsIntoRow(data, flatContributors)
}

export default function ContributorsDataIntegrator({ gatherers, metrics, Renderer, onModelProviderIds, rendererProps = {} }) {
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  const nonIntradayGatherers = useMemo(() => (gatherers || []).filter((g) => g.type !== "intraday"), [gatherers])
  const intradayGatherers = useMemo(() => (gatherers || []).filter((g) => g.type === "intraday"), [gatherers])
  const intradayPriceGatherers = useMemo(
    () => intradayGatherers.filter((g) => g.streamType === "prices"),
    [intradayGatherers],
  )
  const intradayStandaloneGatherers = useMemo(
    () => intradayGatherers.filter((g) => g.streamType === "standalone-performance"),
    [intradayGatherers],
  )
  const combine = useCallback(results => {
    const status = combineTanStackStatuses(results)
    let data
    if (status === OK) {
      data = zipResults(map(results, r => r.data))
    }

    return { status, data }
  }, [])

  const queries = map(nonIntradayGatherers, ({ key, query, variables, select }) => ({
    queryKey: [key],
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, resolve, reject))
    }),
    select,
  }))

  const { status, data } = useCombinedQueries(queries, combine)

  const modelProviderIds = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || intradayGatherers.length === 0 || !data || !data.children) {
      return EMPTY_ARRAY
    }
    return collectLeafModelProviderIds(data)
  }, [data, isIntradayAnalyticsEnabled, intradayGatherers])

  const shouldEnableIntraday =
    isIntradayAnalyticsEnabled
    && intradayGatherers.length > 0
    && !!data
    && modelProviderIds.length > 0
  const { prices: intradayPrices, lastUpdatedAt } = useIntradayPriceStream({
    modelProviderIds,
    modelId: intradayPriceGatherers[0]?.modelId,
    enabled: shouldEnableIntraday && intradayPriceGatherers.length > 0,
  })

  const standaloneSubscriptions = useMemo(() => {
    if (!shouldEnableIntraday || intradayStandaloneGatherers.length === 0) {
      return []
    }

    const periods = [...new Set(intradayStandaloneGatherers.map((g) => g.period).filter(Boolean))]
    return periods.map((period) => ({
      period,
      modelProviderIds,
    }))
  }, [intradayStandaloneGatherers, modelProviderIds, shouldEnableIntraday])

  const { standalonePerformanceByPeriod } = useIntradayStandalonePerformanceStream({
    subscriptions: standaloneSubscriptions,
    modelId: intradayStandaloneGatherers[0]?.modelId,
    enabled: shouldEnableIntraday && standaloneSubscriptions.length > 0,
  })

  const dataWithIntraday = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || intradayGatherers.length === 0 || !data) {
      return data
    }
    if (isEmpty(intradayPrices) && isEmpty(standalonePerformanceByPeriod)) {
      return data
    }

    const intradayPricesByRowId = buildIntradayFlatMap(intradayPrices)
    const priceContributors = map(intradayPriceGatherers, (g) => ({
      prefix: g.prefix,
      valuesByRowId: intradayPricesByRowId,
    }))

    const standaloneValuesByPeriod = intradayStandaloneGatherers.reduce((valuesByPeriod, gatherer) => {
      const periodKey = String(gatherer.period)
      if (!valuesByPeriod.has(periodKey)) {
        valuesByPeriod.set(periodKey, buildIntradayStandaloneFlatMap(standalonePerformanceByPeriod, gatherer.period))
      }
      return valuesByPeriod
    }, new Map())

    const standaloneContributors = map(intradayStandaloneGatherers, (g) => ({
      prefix: g.prefix,
      valuesByRowId: standaloneValuesByPeriod.get(String(g.period)) || {},
    }))
    return mergeFlatContributors(data, [...priceContributors, ...standaloneContributors])
  }, [
    data,
    intradayPrices,
    intradayPriceGatherers,
    intradayStandaloneGatherers,
    standalonePerformanceByPeriod,
    isIntradayAnalyticsEnabled,
    intradayGatherers,
  ])

  const metricsWithIntradayTooltip = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || intradayGatherers.length === 0) {
      return metrics
    }

    return addIntradayPriceTooltipContents(metrics, lastUpdatedAt)
  }, [metrics, lastUpdatedAt, isIntradayAnalyticsEnabled, intradayGatherers])

  useEffect(() => {
    if (typeof onModelProviderIds !== "function") {
      return
    }
    onModelProviderIds(modelProviderIds)
  }, [modelProviderIds, onModelProviderIds])

  return <Renderer status={status} data={dataWithIntraday} metrics={metricsWithIntradayTooltip} {...rendererProps} />
}

const GRAPHQL_GATHERER_SHAPE = PropTypes.shape({
  key: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  query: PropTypes.string.isRequired,
  variables: PropTypes.object.isRequired,
  select: PropTypes.func.isRequired,
})

const INTRADAY_GATHERER_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf(["intraday"]).isRequired,
  key: PropTypes.arrayOf(PropTypes.string).isRequired,
  modelId: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  streamType: PropTypes.oneOf(["prices", "standalone-performance"]).isRequired,
  period: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
})

ContributorsDataIntegrator.propTypes = {
  gatherers: PropTypes.arrayOf(PropTypes.oneOfType([GRAPHQL_GATHERER_SHAPE, INTRADAY_GATHERER_SHAPE])).isRequired,
  metrics: PropTypes.array.isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
  onModelProviderIds: PropTypes.func,
}
